// src/features/services/ttlock/api/locks.ts
import { http } from './client'

export interface Lock {
  lockId: number
  lockName: string
  lockMac: string
  lockAlias: string | null
  lockFlag: 0 | 1
  hasGateway?: 0 | 1
  /** Battery percentage (0-100). TTLock API often returns `electricQuantity`. */
  electricQuantity?: number
  /** Some backends might typo the field; accept it defensively. */
  electricQuanitty?: number
}

export interface LockRecord {
  recordId: number
  lockId: number
  lockDate: number
  recordType: number
}

export type OpenState = 0 | 1 | 2 // 0 locked, 1 unlocked, 2 unknown/offline

// Extended: also returns per-lock status (online/state/error) now provided by backend.
// We keep signature Promise<Lock[]> but each Lock object is augmented with an internal `_status` field.
// TypeScript callers can cast if they want richer info.
export function listLocks(page = 1, size = 20): Promise<Lock[]> {
  return http<{ locks: Lock[]; statuses?: Array<{ lockId: number; state: number; online: boolean; stale: boolean; lastStateAt: number; errorCode: number | null; errorMessage: string | null }> }>(
    '/bff/ttlock/api/locks',
    undefined,
    { page, size }
  ).then(r => {
    if (!r.statuses) return r.locks;
    const map = new Map(r.statuses.map(s => [s.lockId, s]));
    return r.locks.map(l => Object.assign(l, { _status: map.get(l.lockId) }));
  });
}

export function getOpenState(lockId: number): Promise<{ state: OpenState }> {
  return http<{ state: OpenState }>(`/bff/ttlock/api/locks/${lockId}/open-state`)
}

export function lock(lockId: number) {
  return http<any>(`/bff/ttlock/api/locks/${lockId}/lock`, { method: 'POST' })
}

export function unlock(lockId: number) {
  return http<any>(`/bff/ttlock/api/locks/${lockId}/unlock`, { method: 'POST' })
}

/** Server returns { action: "lock"|"unlock", before, result }.
 *  We map action → "locked" | "unlocked" for friendlier UI.
 */
export function toggleLock(
  lockId: number
): Promise<{ action: 'locked' | 'unlocked'; stateBefore: OpenState; result?: any }> {
  return http<{ action: 'lock' | 'unlock'; before: OpenState; result?: any }>(
    `/bff/ttlock/api/locks/${lockId}/toggle`,
    { method: 'POST' }
  ).then(({ action, before, result }) => ({
    action: action === 'unlock' ? 'unlocked' : 'locked',
    stateBefore: before,
    result,
  }))
}

/* Optional helper */
export async function getFleetStatus(): Promise<[number, number]> {
  const locks = await listLocks(1, 500)
  const online = locks.filter(l => l.lockFlag === 1).length
  return [online, locks.length - online]
}